import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { Button } from '../../../components/button/button';
import SelectField from '../../../components/form-fields/select-field/select-field';
import DateRangePickerField from '../../../components/form-fields/date-range-picker/date-range-picker';
import { fetchUsers } from '../../../services/usersServices';
import { downloadUserTimesheetWorkbook } from './export-utils';
import { ExportUserTimesheetModalStyled } from './export-user-timesheet-modal.styles';

const ExportUserTimesheetModal = ({ open, onClose, defaultDateRange }) => {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [dateRange, setDateRange] = useState(defaultDateRange || []);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState('');

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['users', 'export-user-timesheet-modal'],
    queryFn: fetchUsers,
    enabled: open,
    refetchOnWindowFocus: false,
  });

  const userOptions = useMemo(() => {
    return users
      .filter((u) => u.firstName && u.surname)
      .map((u) => ({
        value: u.id,
        label: u.fullName || `${u.firstName} ${u.surname}`,
        category: u.serviceGroup || 'Other',
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [users]);

  const selectedUser = useMemo(
    () => users.find((u) => u.id === selectedUserId),
    [users, selectedUserId],
  );

  const hasValidRange = dateRange && dateRange[0] && dateRange[1];
  const canDownload = !!selectedUser && hasValidRange && !isDownloading;

  const handleClose = () => {
    if (isDownloading) return;
    setSelectedUserId('');
    setDateRange(defaultDateRange || []);
    setError('');
    onClose();
  };

  const handleDownload = async () => {
    if (!canDownload) return;
    setError('');
    setIsDownloading(true);
    try {
      await downloadUserTimesheetWorkbook({
        user: selectedUser,
        startDate: dayjs(dateRange[0]).toDate(),
        endDate: dayjs(dateRange[1]).toDate(),
      });
      handleClose();
    } catch (err) {
      console.error('Failed to download timesheet workbook:', err);
      setError(err.message || 'Failed to generate the Excel file. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Export User Timesheet</DialogTitle>
      <DialogContent dividers>
        <ExportUserTimesheetModalStyled>
          <div className="row">
            <SelectField
              name="exportUser"
              label="User"
              isRequired
              placeholder={usersLoading ? 'Loading users...' : 'Select a user'}
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              options={userOptions}
              isGrouped
              disabled={usersLoading}
            />
          </div>

          <div className="row">
            <DateRangePickerField
              name="exportDateRange"
              label="Date range"
              isRequired
              defaultValue={defaultDateRange}
              onChange={setDateRange}
            />
            <Typography variant="caption" className="helper">
              One Excel file with two sheets will be downloaded: raw entries, and
              per-project hours as the Utilization Dashboard classifies them.
            </Typography>
          </div>

          {error && (
            <Typography variant="caption" className="error">
              {error}
            </Typography>
          )}
        </ExportUserTimesheetModalStyled>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose} disabled={isDownloading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleDownload}
          disabled={!canDownload}
          isLoading={isDownloading}
        >
          Download Extract
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportUserTimesheetModal;
